/**
 * Milestones Service
 *
 * API endpoints for milestone operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { Milestone } from '@medsuite/models';

export interface CreateMilestoneDto {
  investment_id: number;
  sequence: number;
  title: string;
  expected_date: Date | string;
}

export interface UpdateMilestoneDto extends Partial<Omit<CreateMilestoneDto, 'investment_id'>> {}

/**
 * Milestones Service
 */
class MilestonesService {
  /**
   * Get all milestones for an investment
   */
  async getAll(investmentId: number): Promise<ApiResponse<Milestone[]>> {
    return apiClient.get<Milestone[]>(`/investments/${investmentId}/milestones`);
  }

  /**
   * Get single milestone by ID
   */
  async getById(investmentId: number, milestoneId: number): Promise<ApiResponse<Milestone>> {
    return apiClient.get<Milestone>(`/investments/${investmentId}/milestones/${milestoneId}`);
  }

  /**
   * Create new milestone
   */
  async create(
    investmentId: number,
    data: Omit<CreateMilestoneDto, 'investment_id'>
  ): Promise<ApiResponse<Milestone>> {
    return apiClient.post<Milestone>(`/investments/${investmentId}/milestones`, {
      ...data,
      investment_id: investmentId,
    });
  }

  /**
   * Update milestone
   */
  async update(
    investmentId: number,
    milestoneId: number,
    data: UpdateMilestoneDto
  ): Promise<ApiResponse<Milestone>> {
    return apiClient.put<Milestone>(`/investments/${investmentId}/milestones/${milestoneId}`, data);
  }

  /**
   * Delete milestone
   */
  async delete(investmentId: number, milestoneId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/investments/${investmentId}/milestones/${milestoneId}`);
  }

  /**
   * Reorder milestones
   */
  async reorder(investmentId: number, milestoneIds: number[]): Promise<ApiResponse<Milestone[]>> {
    return apiClient.patch<Milestone[]>(`/investments/${investmentId}/milestones/reorder`, {
      milestone_ids: milestoneIds,
    });
  }
}

export const milestonesService = new MilestonesService();
